import {
    CLICK_EVENT,
    HIDE_CSS_CLASS,
    DISPLAY_CSS_CLASS,
    DISABLE_CSS_CLASS,
    CLICKED_CSS_CLASS,
    CATEGORY_FILTER_NAME,
    REGION_FILTER_NAME,
    REGION_ZIPCODE_FILTER_NAME,
    REGION_DISTANCE_FILTER_NAME,
    JOBTYPE_FILTER_NAME,
    WORKPERIOD_FILTER_NAME,
    FREE_TEXT_FILTER_NAME,
    URL_PARAMS_SEPERATOR,
} from './constants';
import Filter from './Filter';
import RegionFilter from './RegionFilter';
import FreeTextFilter from './FreeTextFilter';
import SelectedFilters from './SelectedFilters';

export default class JobFilter {
    constructor(elm, args) {
        this.elm = elm;
        this.desktopBreakpoint =
            window.matchMedia('(min-width:992px)')?.matches;

        // callback to get parents current pagination
        this.getPagination = args?.getPagination;
        this.setPagination = args?.setPagination;
        this.onDisplayMobileMenu = args?.onDisplayMobileMenu;
        this.onCloseMobileMenu = args?.onCloseMobileMenu;

        // reset callback
        this.onReset = args?.onReset;

        this.onChangeFiltersCallback = args?.onChangeFilters;

        // external element to fetchMoreJobs
        this.externalFetchMoreBtn = args?.fetchMoreBtn;

        // External element to display mobileMenu on click
        this.displayMobileMenuOnClickElement =
            args?.displayMobileMenuOnClickElement;
        const displayDropdownCallback = this.displayDropdown.bind(this);
        const displayMobileMenuCallback = this.displayMobileMenu.bind(this);

        this.utils = args.utils;
        this.utils?.setDisableFetchJobs(args?.disableFetch ?? false);
        this.utils?.addCallbackToOnLoading(
            this.toggleLoadingSpinner.bind(this),
        );
        this.utils.addCallbackToOnFetchResult(
            function ({ totalItems = 0 } = {}) {
                this.setSearchBtnText(totalItems);
            }.bind(this),
        );

        this.selectedFilters = new SelectedFilters({
            elm,
            updateBadges: this.updateBadges.bind(this),
            onChangeFilters: this.onChangeFilters.bind(this),
            toggleResetFiltersBtn: this.toggleResetFiltersBtn.bind(this),
        });

        this.categoryFilter = new Filter({
            elm: elm,
            filterDataAttr: 'data-category-filter',
            dropdownDataAttr: 'data-category-dropdown',
            displayDropdownCallback: displayDropdownCallback,
            displayMobileMenuCallback: displayMobileMenuCallback,
        });

        this.regionFilter = new RegionFilter({
            elm: elm,
            displayDropdownCallback: displayDropdownCallback,
            displayMobileMenuCallback: displayMobileMenuCallback,
            addToSelectedFilters: function (
                inputNode,
                removeKeywordCallback,
                fetchQuery = true,
                customSpan,
            ) {
                this.selectedFilters.addToSelectedFilters({
                    inputNode,
                    filterName: REGION_FILTER_NAME,
                    removeKeywordCallback,
                    fetchQuery: fetchQuery,
                    customSpan: customSpan,
                });
            }.bind(this),
            removeFromSelectedFilters: function (id, callback) {
                this.selectedFilters.removeFromSelectedFilters({
                    id,
                    filterName: REGION_FILTER_NAME,
                    callback: callback,
                });
            }.bind(this),
        });

        this.jobTypeFilter = new Filter({
            elm: elm,
            filterDataAttr: 'data-job-type-filter',
            dropdownDataAttr: 'data-job-type-dropdown',
            displayDropdownCallback: displayDropdownCallback,
            displayMobileMenuCallback: displayMobileMenuCallback,
        });

        this.workPeriodFilter = new Filter({
            elm: elm,
            filterDataAttr: 'data-work-period-filter',
            dropdownDataAttr: 'data-work-period-dropdown',
            displayDropdownCallback: displayDropdownCallback,
            displayMobileMenuCallback: displayMobileMenuCallback,
        });

        this.freeTextFilter = new FreeTextFilter({
            elm,
            addToSelectedFilters: function ({
                inputNode,
                customSpan,
                removeKeywordCallback,
            }) {
                this.selectedFilters.addToSelectedFilters({
                    inputNode,
                    filterName: FREE_TEXT_FILTER_NAME,
                    removeKeywordCallback,
                    updateBadge: false,
                    includeInCount: false,
                    customSpan,
                });
            }.bind(this),
            removeFromSelectedFilters:
                this.selectedFilters.removeFromSelectedFilters.bind(
                    this.selectedFilters,
                ),
            openDropDownCallback: this.cleanupDropdownMenu.bind(this),
        });

        this.resetFiltersBtn = elm?.querySelector('[data-btn-reset]');
        this.searchBtn = elm?.querySelector('[data-btn-search]');
        this.searchBtnText =
            this?.searchBtn?.querySelector('[data-search-text]');
        this.searchBtnSpinner = this?.searchBtn?.querySelector(
            '[data-search-spinner]',
        );

        this.filtersContainerDiv = elm?.querySelector('[data-filter-menu]');
        this.mobileMenuCloseBtn = this?.filtersContainerDiv?.querySelector(
            '[data-close-menu-btn]',
        );
        this.mobileSearchBtn = elm?.querySelector('[data-mobile-search-btn]');

        const fetchJobs = this.utils?.fetchQuery.bind(this.utils);
        const closeMobileMenu = this.closeMobileMenu.bind(this);

        this.mobileMenuCloseBtn?.addEventListener(CLICK_EVENT, closeMobileMenu);

        this.resetFiltersBtn?.addEventListener(
            CLICK_EVENT,
            this.resetAllFilters.bind(this),
        );

        this.searchBtn?.addEventListener(CLICK_EVENT, function () {
            closeMobileMenu();
            args?.onSearchBtnClick();
        });
        this.mobileSearchBtn?.addEventListener(
            CLICK_EVENT,
            args?.onSearchBtnClick,
        );

        if (this.displayMobileMenuOnClickElement) {
            this.displayMobileMenuOnClickElement?.addEventListener(
                CLICK_EVENT,
                this.displayMobileMenu.bind(this),
            );
        }
        if (this.externalFetchMoreBtn) {
            this.externalFetchMoreBtn?.addEventListener(
                CLICK_EVENT,
                function () {
                    const { page } = this.getPagination();
                    this.setPagination(page + 1);
                    fetchJobs({ fetchMore: true });
                }.bind(this),
            );
        }

        // Close filter dropdown on window click
        window.addEventListener(
            CLICK_EVENT,
            function (e) {
                if (
                    (!e.target.closest(
                        '.filter-item__dropdown, .filter-item',
                    ) ||
                        e.target.closest('.filter-item.search-btn')) &&
                    this.currentDisplayedDropdown?.classList.contains(
                        DISPLAY_CSS_CLASS,
                    )
                ) {
                    this.cleanupDropdownMenu();
                }
            }.bind(this),
        );

        this.setFiltersFromUrlParams({
            forceFetch: args?.fetchOnInit ?? true,
        });

        this.body = document.querySelector('[data-body]');
    }

    setSearchBtnText(results) {
        if (results >= 0) {
            this.searchBtnText.innerText = `${results} ${this.searchBtnText.attributes['data-text-result'].value}`;
        } else {
            this.searchBtnText.innerText =
                this.searchBtnText.attributes['data-text-val'].value;
        }
    }

    displayDropdown(e, clickedFilter, currentDropDown, dropdownInputs) {
        if (e.target.closest('.filter-item__dropdown') === currentDropDown) {
            return;
        }
        const dropdownAlreadyOpen = this.currentClickedFilter === clickedFilter;
        if (this.currentClickedFilter) {
            this.cleanupDropdownMenu();
        }
        if (!dropdownAlreadyOpen) {
            this.currentClickedFilter = clickedFilter;
            this.currentDisplayedDropdown = currentDropDown;
            this.currentDropDownInputs = dropdownInputs;
            this.currentClickedFilter.classList.toggle(CLICKED_CSS_CLASS);
            this.currentDisplayedDropdown.classList.toggle(DISPLAY_CSS_CLASS);
            this.addCheckmarkClickListener()?.bind(this);
            if (
                clickedFilter === this.regionFilter.filterBtn &&
                this.desktopBreakpoint
            ) {
                this.regionFilter.focusInputField();
            }
        }
    }

    cleanupDropdownMenu() {
        if (this.currentDisplayedDropdown) {
            this.removeCheckmarkClickListener();
            this.currentDisplayedDropdown?.classList.remove(DISPLAY_CSS_CLASS);
            this.currentDisplayedDropdown = null;
            this.currentClickedFilter?.classList?.toggle(CLICKED_CSS_CLASS);
            this.currentClickedFilter = null;
        }
    }

    displayMobileMenu(e, clickedFilter, currentDropDown, dropdownInputs) {
        this.filtersContainerDiv.classList.toggle('show');
        if (clickedFilter) {
            this.displayDropdown(
                e,
                clickedFilter,
                currentDropDown,
                dropdownInputs,
            );
        }
        this.onDisplayMobileMenu?.();
        if (this.filtersContainerDiv.classList.contains('show')) {
            this.body.classList.add('filter-open');
        }
    }

    closeMobileMenu() {
        if (this.filtersContainerDiv.classList.contains('show')) {
            this.filtersContainerDiv.classList.remove('show');
            this.body.classList.remove('filter-open');
            this.cleanupDropdownMenu();
            this.onCloseMobileMenu?.();
        }
    }

    setOnChangeFiltersCallback(callback) {
        this.onChangeFiltersCallback = callback;
    }

    toggleLoadingSpinner() {
        this.searchBtnSpinner?.classList?.toggle(HIDE_CSS_CLASS);
    }

    handleCheckmarkContainerClick(e) {
        let currentTarget = e.target;

        // avoid empty cell clicks to update inputs
        if (currentTarget.classList.contains('input-container')) {
            return;
        }

        const targetWasLabel = currentTarget.nodeName === 'LABEL';

        // update input manually if the outer input container has been clicked
        if (currentTarget.nodeName !== 'INPUT') {
            if (currentTarget.nodeName !== 'P') {
                currentTarget = currentTarget?.querySelector('input');
                currentTarget.checked = !currentTarget?.checked;
            }
            if (targetWasLabel) {
                // since the label is wrapped around the input, a click will trigger
                // a second input click as well, which we prevent here
                e.preventDefault();
            }
        }

        const filterKey =
            this.currentClickedFilter === this.regionFilter.filterBtn
                ? REGION_FILTER_NAME
                : this.currentClickedFilter === this.categoryFilter.filterBtn
                ? CATEGORY_FILTER_NAME
                : this.currentClickedFilter === this.workPeriodFilter.filterBtn
                ? WORKPERIOD_FILTER_NAME
                : JOBTYPE_FILTER_NAME;

        const { checked, id } = currentTarget;
        if (checked) {
            this.selectedFilters.addToSelectedFilters({
                inputNode: currentTarget,
                filterName: filterKey,
            });
        } else {
            this.selectedFilters.removeFromSelectedFilters({
                id: id,
                filterName: filterKey,
            });
        }
    }

    addCheckmarkClickListener() {
        this.currentDisplayedInputContainer =
            this.currentDisplayedDropdown.querySelector('.input-container');
        this.boundInputListener = this.handleCheckmarkContainerClick.bind(this);
        this.currentDisplayedInputContainer.addEventListener(
            CLICK_EVENT,
            this.boundInputListener,
        );
    }

    removeCheckmarkClickListener() {
        this.currentDisplayedInputContainer.removeEventListener(
            CLICK_EVENT,
            this.boundInputListener,
        );
    }

    resetAllFilters() {
        // abort any ongoing fetch
        this.utils?.abortFetch();

        // reset all filters
        [CATEGORY_FILTER_NAME, REGION_FILTER_NAME, JOBTYPE_FILTER_NAME, WORKPERIOD_FILTER_NAME].forEach(
            (filterName) => {
                Object.values(
                    this.selectedFilters.getFilterByName(filterName),
                ).forEach(({ inputNode: { id } }) =>
                    this.selectedFilters.removeFromSelectedFilters({
                        id: id,
                        filterName: filterName,
                        fetchQuery: false,
                    }),
                );
            },
        );
        this.utils?.updateURLParams();
        this.regionFilter.resetSlider();
        this.freeTextFilter.closeDropDown();
        this.setSearchBtnText();
        this?.onReset();
        this.utils?.fetchQuery();
    }

    toggleResetFiltersBtn() {
        const resetbtnClassList = this.resetFiltersBtn?.classList;
        const divIsHidden = resetbtnClassList.contains(DISABLE_CSS_CLASS);
        if (this.selectedFilters.anySelected()) {
            divIsHidden &&
                this.resetFiltersBtn.classList.remove(DISABLE_CSS_CLASS);
        } else {
            !divIsHidden && resetbtnClassList.add(DISABLE_CSS_CLASS);
        }
    }

    onChangeFilters(fetch = true) {
        const selectedFiltersCount = this.selectedFilters.getValuesCount();
        this.onChangeFiltersCallback?.(selectedFiltersCount);
        if (selectedFiltersCount > 0) {
            this.addSelectedFiltersToUrlParams();
        } else {
            this.utils?.updateURLParams();
            this?.onReset();
        }
        fetch && this.utils?.fetchQuery();
    }

    updateBadges(filterName) {
        const filter = this.getFilterByName(filterName);
        this.utils?.setFilterBadgeNumbers(
            [filter.filterBtn, filter.mobileFilterBtn],
            this.selectedFilters.getCount(filterName),
        );
    }

    getFilterByName(filterName) {
        switch (filterName) {
            case CATEGORY_FILTER_NAME:
                return this.categoryFilter;
            case REGION_FILTER_NAME:
                return this.regionFilter;
            case JOBTYPE_FILTER_NAME:
                return this.jobTypeFilter;
            case WORKPERIOD_FILTER_NAME:
                return this.workPeriodFilter;
            default:
                return null;
        }
    }

    addSelectedFiltersToUrlParams() {
        const urlParams = new URLSearchParams();
        [CATEGORY_FILTER_NAME, REGION_FILTER_NAME, JOBTYPE_FILTER_NAME, WORKPERIOD_FILTER_NAME].forEach(
            (filterName) =>
                this.selectedFilters.addCheckmarkInputsToUrlParams(
                    urlParams,
                    filterName,
                ),
        );
        const zipcodeValue = this.regionFilter?.zipcodeInput?.value;
        const zipcodeRadiusValue = this.regionFilter?.rangeSlider?.value;
        if (zipcodeValue) {
            urlParams.append(REGION_ZIPCODE_FILTER_NAME, zipcodeValue);
            if (zipcodeRadiusValue) {
                urlParams.append(
                    REGION_DISTANCE_FILTER_NAME,
                    zipcodeRadiusValue,
                );
            }
        }

        const freeTextValue = this.freeTextFilter.textInput.value;
        if (freeTextValue) {
            urlParams.append(FREE_TEXT_FILTER_NAME, freeTextValue);
        }
        this.utils?.updateURLParams(urlParams.toString());
    }

    setFilterCheckmarksFromParams(urlSearchParams, filterName) {
        const ids = urlSearchParams
            .getAll(filterName)[0]
            ?.split(URL_PARAMS_SEPERATOR);
        const foundInputNodes =
            this.getFilterByName(filterName)?.setCheckmarkInputsByName(ids);
        foundInputNodes?.forEach((inputNode) => {
            this.selectedFilters.addToSelectedFilters({
                inputNode: inputNode,
                filterName: filterName,
                fetchQuery: false,
            });
        });
    }

    setFiltersFromUrlParams({ forceFetch = false }) {
        const currentParams = this.utils?.getUrlParams();
        if (currentParams.has(CATEGORY_FILTER_NAME)) {
            this.setFilterCheckmarksFromParams(
                currentParams,
                CATEGORY_FILTER_NAME,
            );
        }
        if (currentParams.has(REGION_FILTER_NAME)) {
            this.setFilterCheckmarksFromParams(
                currentParams,
                REGION_FILTER_NAME,
            );
        }
        if (currentParams.has(JOBTYPE_FILTER_NAME)) {
            this.setFilterCheckmarksFromParams(
                currentParams,
                JOBTYPE_FILTER_NAME,
            );
        }
        if (currentParams.has(WORKPERIOD_FILTER_NAME)) {
            this.setFilterCheckmarksFromParams(
                currentParams,
                WORKPERIOD_FILTER_NAME,
            );
        }
        if (currentParams.has(REGION_ZIPCODE_FILTER_NAME)) {
            const zipcodeValue = currentParams.get(REGION_ZIPCODE_FILTER_NAME);
            if (zipcodeValue) {
                this.regionFilter.setZipcodeValue(zipcodeValue);
                if (currentParams.has(REGION_DISTANCE_FILTER_NAME)) {
                    const radiusValue = currentParams.get(
                        REGION_DISTANCE_FILTER_NAME,
                    );
                    this.regionFilter.setRangeSliderValue(radiusValue);
                }
                this.regionFilter.updateZipcode(false);
            }
        }

        if (currentParams.has(FREE_TEXT_FILTER_NAME)) {
            this.freeTextFilter.textInput.value = currentParams.get(
                FREE_TEXT_FILTER_NAME,
            );
            this.freeTextFilter.updateFreeTextInput();
            this.freeTextFilter.openDropDown();
        }

        if (forceFetch || this.selectedFilters.anySelected()) {
            this.utils?.fetchQuery();
        }
    }
}
